import { loadMapsApi, maps, MAP_OPTIONS } from '@js/modules/maps/google-maps';
// Store Finder: client-side search + type filtering over the rendered store cards,
// with the map markers hidden and re-framed in step.
//
// The cards' data attributes are the only source of truth — nothing is duplicated
// into a JSON payload, so the DOM and the map can never disagree.
const DEBOUNCE_MS = 150;
const FOCUS_ZOOM = 12;
const readEntries = (root) => Array.from(root.querySelectorAll('.store-card')).map((card) => {
    const lat = parseFloat(card.dataset.lat || '');
    const lng = parseFloat(card.dataset.lng || '');
    return {
        card,
        types: (card.dataset.types || '').split(' ').filter(Boolean),
        haystack: card.dataset.search || '',
        lat: Number.isNaN(lat) ? null : lat,
        lng: Number.isNaN(lng) ? null : lng,
        marker: null,
    };
});
const initBlock = (root) => {
    const entries = readEntries(root);
    if (entries.length === 0) {
        return;
    }
    const searchInput = root.querySelector('[data-store-search]');
    const filters = Array.from(root.querySelectorAll('[data-store-filter]'));
    const emptyMessage = root.querySelector('[data-store-empty]');
    const form = root.querySelector('.store-finder__search');
    const mapElement = root.querySelector('.store-finder__map[data-maps-key]');
    let map = null;
    let infoWindow = null;
    // Frame the map around whatever is currently visible.
    const fitVisible = (visible) => {
        if (!map) {
            return;
        }
        const located = visible.filter((entry) => entry.lat !== null && entry.lng !== null);
        if (located.length === 0) {
            return;
        }
        if (located.length === 1) {
            map.setCenter({ lat: located[0].lat, lng: located[0].lng });
            map.setZoom(FOCUS_ZOOM);
            return;
        }
        const bounds = new (maps().LatLngBounds)();
        located.forEach((entry) => bounds.extend({ lat: entry.lat, lng: entry.lng }));
        map.fitBounds(bounds);
    };
    const apply = () => {
        const term = ((searchInput === null || searchInput === void 0 ? void 0 : searchInput.value) || '').trim().toLowerCase();
        const active = filters.filter((input) => input.checked).map((input) => input.value);
        // With no checkboxes rendered, every store passes the type test.
        const filtering = filters.length > 0;
        const visible = [];
        entries.forEach((entry) => {
            const matchesTerm = !term || entry.haystack.indexOf(term) !== -1;
            const matchesType = !filtering
                || entry.types.length === 0
                || entry.types.some((type) => active.indexOf(type) !== -1);
            const shown = matchesTerm && matchesType;
            entry.card.classList.toggle('is-hidden', !shown);
            entry.card.classList.remove('is-active');
            if (entry.marker) {
                entry.marker.setMap(shown ? map : null);
            }
            if (shown) {
                visible.push(entry);
            }
        });
        if (emptyMessage) {
            emptyMessage.hidden = visible.length > 0;
        }
        infoWindow === null || infoWindow === void 0 ? void 0 : infoWindow.close();
        fitVisible(visible);
    };
    let debounce;
    searchInput === null || searchInput === void 0 ? void 0 : searchInput.addEventListener('input', () => {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(apply, DEBOUNCE_MS);
    });
    filters.forEach((input) => input.addEventListener('change', apply));
    // Filtering is live, so the button has nothing left to submit.
    form === null || form === void 0 ? void 0 : form.addEventListener('submit', (event) => {
        event.preventDefault();
        window.clearTimeout(debounce);
        apply();
    });
    if (!mapElement) {
        return;
    }
    const key = mapElement.dataset.mapsKey || '';
    const zoom = parseInt(mapElement.dataset.zoom || '7', 10) || 7;
    loadMapsApi(key)
        .then(() => {
        map = new (maps().Map)(mapElement, Object.assign(Object.assign({}, MAP_OPTIONS), { center: { lat: 51.1657, lng: 10.4515 }, zoom }));
        infoWindow = new (maps().InfoWindow)();
        entries.forEach((entry) => {
            var _a;
            if (entry.lat === null || entry.lng === null) {
                return;
            }
            const name = ((_a = entry.card.querySelector('.store-card__name')) === null || _a === void 0 ? void 0 : _a.textContent) || '';
            entry.marker = new (maps().Marker)({
                position: { lat: entry.lat, lng: entry.lng },
                map,
                title: name,
            });
            // Marker → highlight and scroll to its card.
            entry.marker.addListener('click', () => {
                entries.forEach((other) => other.card.classList.remove('is-active'));
                entry.card.classList.add('is-active');
                entry.card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                infoWindow.setContent(entry.card.innerHTML);
                infoWindow.open(map, entry.marker);
            });
            // Card → focus its marker.
            entry.card.addEventListener('click', () => {
                map.panTo({ lat: entry.lat, lng: entry.lng });
                map.setZoom(FOCUS_ZOOM);
                infoWindow.setContent(entry.card.innerHTML);
                infoWindow.open(map, entry.marker);
            });
            entry.card.classList.add('is-clickable');
        });
        apply();
    })
        .catch(() => {
        // Leave the styled placeholder rather than a broken map; the list and
        // the filters keep working without it.
    });
};
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.block.store-finder').forEach(initBlock);
});
